import React from 'react';
import { useFetchResultQuery } from '../../api/result';
import { useFetchResultCompareQuery } from '../../api/resultCompare';
import { useFetchImageQuery } from '../../api/image';
import { useFetchProjectQuery } from '../../api/project';
import LoadingSpiner from '../Loader/LoadingSpiner';
import { FaXmark } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { selectResultIds } from '../../Store/resultSlice';
import { ResultData } from '../../types/report';
import DataCell from './Elements/DataCell';
import { TbRulerMeasure } from 'react-icons/tb';
import ResultMeasurementPieChart from './ResultMeasurementPieChart';
import colorMap, { qualityColors } from './measurementColors';
import { measurementClasses } from './measurementClasses';
import { obfuscateImageName } from '../../utils/sizeToText';

interface ResultViewerSidebarProps {
    resultId: string;
    compareId?: string;
    dataIndex?: number;
}

const ResultViewerSidebar: React.FC<ResultViewerSidebarProps> = ({ resultId, compareId, dataIndex = 0 }) => {
    const { data: result, isLoading, isError, error: resultError } = useFetchResultQuery(resultId);
    const { data: resultCompare, isLoading: isLoadingCompare, isError: isErrorCompare, error: compareError } = useFetchResultCompareQuery(compareId, {
        skip: !compareId
    });
    const { data: image, isLoading: isLoadingImage, isError: isErrorImage, error: imageError } = useFetchImageQuery(result?.image_id);
    const { data: project, isLoading: isLoadingProject, isError: isErrorProject, error: projectError } = useFetchProjectQuery(image?.project_id);
    const { data: imageCompare, isLoading: isLoadingImageCompare, isError: isErrorImageCompare, error: imageCompareError } = useFetchImageQuery(resultCompare?.image_id, {
        skip: !resultCompare?.image_id
    });
    const { data: projectCompare, isLoading: isLoadingProjectCompare, isError: isErrorProjectCompare, error: projectCompareError } = useFetchProjectQuery(imageCompare?.project_id, {
        skip: !imageCompare?.project_id
    });

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const loading = isLoading || isLoadingCompare || isLoadingImage || isLoadingProject || isLoadingImageCompare || isLoadingProjectCompare;
    const error = isError || isErrorCompare || isErrorImage || isErrorProject || isErrorImageCompare || isErrorProjectCompare;

    // Log errors if they occur
    React.useEffect(() => {
        if (isError) console.error('Result Error:', resultError);
        if (isErrorCompare) console.error('Compare Error:', compareError);
        if (isErrorImage) console.error('Image Error:', imageError);
        if (isErrorProject) console.error('Project Error:', projectError);
        if (isErrorImageCompare) console.error('Image Compare Error:', imageCompareError);
        if (isErrorProjectCompare) console.error('Project Compare Error:', projectCompareError);
    }, [isError, isErrorCompare, isErrorImage, isErrorProject, isErrorImageCompare, isErrorProjectCompare, 
        resultError, compareError, imageError, projectError, imageCompareError, projectCompareError]);

    if (loading) return <LoadingSpiner />;
    if (error) return <p>Error loading result</p>;
    if (!result) return null;

    const handleResultSelect = (resultId: string, coreIndex?: number) => {
        const url = `/result/${resultId}${coreIndex !== undefined ? `?dataIndex=${coreIndex}` : ''}`;
        navigate(url);
        dispatch(selectResultIds([resultId]));
    };

    const renderResultDataItems = (resultData: ResultData[]) => {
        // Only render the item at dataIndex
        const item = resultData[dataIndex];
        if (!item) return null;

        return (
            <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">Core {dataIndex + 1}</h3>
                <div className="space-y-2">
                    {Object.entries(item).map(([key, value]) => {
                        if (key === 'Images') return null;
                        // Handle object values by converting them to string
                        const displayValue = typeof value === 'object' ? JSON.stringify(value) : value;
                        return (
                            <div key={key} className="flex justify-between">
                                <span className="font-medium">{key}:</span>
                                <span>{displayValue}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const measurement = result?.result_data?.[dataIndex]?.['AI-Measurement'] || {};
    const measurementKeys = Object.keys(measurement).sort().filter(key => !['withBackground', 'withoutBackground'].includes(key))
    const withoutBgMeasurementKeys = measurement?.withoutBackground ? Object.keys(measurement?.withoutBackground)?.sort().map( key => {
        return {
        label: measurementClasses[key],
        key}
    }) : []
    console.log("Without Background Measurement Keys:", withoutBgMeasurementKeys);
    const compareMeaurement = resultCompare?.result_data?.[dataIndex]?.['AI-Measurement'] || {};
    const compareWithoutBgMeasurementKeys = compareMeaurement?.withoutBackground ? Object.keys(compareMeaurement?.withoutBackground)?.sort().map( key => {
        return {
        label: measurementClasses[key],
        key}
    }) : []

    const obfuscatedName = obfuscateImageName(result.name);
    const type = result.name.toLowerCase().includes('quality') ? 'Quality' : 'Segmentation';
    const displayName = `${obfuscatedName} - ${type}`;

    return (
        <div className="p-4">
            

         

            <div className="flex flex-col">
                <div className="flex-1">
                    {isLoading ? <LoadingSpiner /> : isError ? <p>Error fetching data</p> : (
                        <div>
                            <DataCell value={displayName} className='font-extrabold'/>
                            <DataCell label={<div><TbRulerMeasure className='inline mr-2'/>Measurements</div>} className='mt-4'/>
                            {withoutBgMeasurementKeys &&
                                withoutBgMeasurementKeys.map(({key, label}: any) => {
                                    // get first word of key
                                    if(!label){
                                        console.warn("key", key, "has no label value")
                                        label = key.replace('Percentage', '');
                                    }

                                    const firstWord = label?.split(' ')[0].toLowerCase() || label || "";
                                    let color = colorMap[firstWord.trim()]
                                    if(type === 'Quality'){
                                        color = qualityColors[firstWord.trim().toLowerCase()]
                                    }

                                    return <DataCell key={key} label={label} value={measurement.withoutBackground?.[key]} color={color} />
                                })
                            }
                            <DataCell label={<ResultMeasurementPieChart data={measurement}/>} />
                        </div>
                    )}
                </div>
                <div className="flex-1 pl-2">
                    <img src={result.imageUrl} alt={result.imageName} className="max-w-full h-auto" />
                </div>
            </div>
        </div>
    );
};

export default ResultViewerSidebar;